import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PencilSquareIcon, EyeIcon, EyeSlashIcon, TrashIcon, XMarkIcon, ChatBubbleLeftRightIcon, BookmarkIcon } from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';

const CATEGORIAS = ['Todas', 'Writing', 'Listening', 'Reading', 'Speaking'];

export default function Actividades({ tipo, nivel }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const tema = getTema(tipo);
  const esProfesor = ['profesor', 'admin'].includes(usuario?.rol);

  const [evaluaciones, setEvaluaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [categoriaActiva, setCategoriaActiva] = useState('Todas');
  const [error, setError] = useState('');

  // Speaking — sesiones
  const [speakingSesiones, setSpeakingSesiones] = useState([]);
  const [speakingCargando, setSpeakingCargando] = useState(false);
  const [speakingTab, setSpeakingTab] = useState('sesiones'); // 'sesiones' | 'grabaciones'
  const [modalCrear, setModalCrear] = useState(false);
  const [modalNotas, setModalNotas] = useState(null);
  const [spTitulo, setSpTitulo] = useState('');
  const [spDesc, setSpDesc] = useState('');
  const [spFecha, setSpFecha] = useState('');
  const [spLink, setSpLink] = useState('');
  const [spMaxAlumnos, setSpMaxAlumnos] = useState(1);
  const [spTipoSesion, setSpTipoSesion] = useState('individual');
  const [spAlumnos, setSpAlumnos] = useState([]);
  const [spNotasEditar, setSpNotasEditar] = useState({});
  const [spGuardando, setSpGuardando] = useState(false);
  const [spGuardadoId, setSpGuardadoId] = useState(null);
  const [spErrorId, setSpErrorId] = useState(null);
  const [reservandoId, setReservandoId] = useState(null);
  const [modalReservas, setModalReservas] = useState(null);

  // Speaking — grabaciones
  const [speakingEntregas, setSpeakingEntregas] = useState([]);
  const [entregasCargando, setEntregasCargando] = useState(false);
  const [modalEntrega, setModalEntrega] = useState(null);
  const [spEntregaTitulo, setSpEntregaTitulo] = useState('');
  const [spEntregaDesc, setSpEntregaDesc] = useState('');
  const [spEntregaLink, setSpEntregaLink] = useState('');
  const [enviandoEntrega, setEnviandoEntrega] = useState(false);
  const [spEntregaNota, setSpEntregaNota] = useState('');
  const [spEntregaComentario, setSpEntregaComentario] = useState('');
  const [calificandoEntrega, setCalificandoEntrega] = useState(false);

  // Dudas
  const [modalDudas, setModalDudas] = useState(null); // { ev } object
  const [dudas, setDudas] = useState([]);
  const [dudasCargando, setDudasCargando] = useState(false);
  const [nuevaDuda, setNuevaDuda] = useState('');
  const [enviandoDuda, setEnviandoDuda] = useState(false);
  const [respondiendoId, setRespondiendoId] = useState(null);
  const [textoRespuesta, setTextoRespuesta] = useState('');
  const [guardandoRespuesta, setGuardandoRespuesta] = useState(false);

  // Plantillas
  const [guardandoPlantillaId, setGuardandoPlantillaId] = useState(null);
  const [plantillaExitoId, setPlantillaExitoId] = useState(null);

  // Drag & drop
  const dragActIdx = useRef(null);
  const dragExamIdx = useRef(null);
  const [dragOverAct, setDragOverAct] = useState(null);
  const [dragOverExam, setDragOverExam] = useState(null);

  const cargar = async () => {
    try {
      setCargando(true);
      const res = await axios.get(`${API_URL}/evaluaciones?tipo=${tipo}&nivel=${nivel}`);
      setEvaluaciones(res.data);
    } catch { setError('Error al cargar'); }
    finally { setCargando(false); }
  };

  const cargarSpeaking = async () => {
    try {
      setSpeakingCargando(true);
      const res = await axios.get(`${API_URL}/speaking?tipo=${tipo}&nivel=${nivel}`);
      setSpeakingSesiones(res.data);
    } catch { /* silent */ }
    finally { setSpeakingCargando(false); }
  };

  const cargarEntregas = async () => {
    try {
      setEntregasCargando(true);
      const res = await axios.get(`${API_URL}/speaking/entregas?tipo=${tipo}&nivel=${nivel}`);
      setSpeakingEntregas(res.data);
    } catch { /* silent */ }
    finally { setEntregasCargando(false); }
  };

  useEffect(() => { cargar(); }, [tipo, nivel]);

  useEffect(() => {
    if (categoriaActiva === 'Speaking') { cargarSpeaking(); cargarEntregas(); }
  }, [categoriaActiva, tipo, nivel]);

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta evaluación? Esta acción no se puede deshacer.')) return;
    setError('');
    try {
      await axios.delete(`${API_URL}/evaluaciones/${id}`);
      setEvaluaciones(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar. Inténtalo de nuevo.');
    }
  };

  const guardarComoPlantilla = async (ev) => {
    setGuardandoPlantillaId(ev.id);
    try {
      const res = await axios.get(`${API_URL}/evaluaciones/${ev.id}`);
      const d = res.data;
      await axios.post(`${API_URL}/plantillas`, {
        titulo: d.titulo,
        descripcion: d.descripcion,
        categoria: d.categoria,
        duracion_minutos: d.duracion_minutos,
        subtipo: d.subtipo,
        subtipo_eval: d.subtipo_eval,
        link_audio: d.link_audio,
        texto_lectura: d.texto_lectura,
        preguntas: (d.preguntas || []).map(p => ({
          texto: p.texto,
          tipo_pregunta: p.tipo_pregunta,
          opciones: p.opciones,
          respuesta_correcta: p.respuesta_correcta,
        })),
      });
      setPlantillaExitoId(ev.id);
      setTimeout(() => setPlantillaExitoId(null), 2500);
    } catch {
      setError('Error al guardar como plantilla');
    } finally {
      setGuardandoPlantillaId(null);
    }
  };

  const abrirDudas = async (ev) => {
    setModalDudas(ev);
    setNuevaDuda('');
    setRespondiendoId(null);
    setTextoRespuesta('');
    setDudasCargando(true);
    try {
      const res = await axios.get(`${API_URL}/dudas/evaluacion/${ev.id}`);
      setDudas(res.data);
      if (!esProfesor) {
        await axios.put(`${API_URL}/dudas/evaluacion/${ev.id}/marcar-leidas`).catch(() => {});
        setEvaluaciones(prev => prev.map(e => e.id === ev.id ? { ...e, dudas_sin_leer: 0 } : e));
      }
    } catch { setDudas([]); }
    finally { setDudasCargando(false); }
  };

  const enviarDuda = async () => {
    if (!nuevaDuda.trim()) return;
    setEnviandoDuda(true);
    try {
      await axios.post(`${API_URL}/dudas`, {
        evaluacion_id: modalDudas.id,
        tipo,
        nivel,
        evaluacion_titulo: modalDudas.titulo,
        pregunta: nuevaDuda.trim(),
      });
      setNuevaDuda('');
      const res = await axios.get(`${API_URL}/dudas/evaluacion/${modalDudas.id}`);
      setDudas(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al enviar la duda');
    } finally { setEnviandoDuda(false); }
  };

  const responderDuda = async (dudaId) => {
    if (!textoRespuesta.trim()) return;
    setGuardandoRespuesta(true);
    try {
      await axios.put(`${API_URL}/dudas/${dudaId}/responder`, { respuesta: textoRespuesta.trim() });
      setRespondiendoId(null);
      setTextoRespuesta('');
      const res = await axios.get(`${API_URL}/dudas/evaluacion/${modalDudas.id}`);
      setDudas(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al responder la duda');
    } finally { setGuardandoRespuesta(false); }
  };

  const eliminarDuda = async (dudaId) => {
    if (!window.confirm('¿Eliminar esta duda?')) return;
    try {
      await axios.delete(`${API_URL}/dudas/${dudaId}`);
      setDudas(prev => prev.filter(d => d.id !== dudaId));
    } catch { /* silent */ }
  };

  const toggleOcultar = async (ev) => {
    try {
      await axios.put(`${API_URL}/evaluaciones/${ev.id}`, {
        titulo: ev.titulo, descripcion: ev.descripcion,
        activa: ev.activa ? 0 : 1, categoria: ev.categoria,
      });
      cargar();
    } catch { setError('Error al actualizar'); }
  };

  const makeDragHandlers = (lista, setLista, dragRef, setDragOver, endpoint) => ({
    onDragStart: (e, i) => { dragRef.current = i; e.dataTransfer.effectAllowed = 'move'; },
    onDragOver:  (e, i) => { e.preventDefault(); setDragOver(i); },
    onDragEnd:   ()     => { setDragOver(null); dragRef.current = null; },
    onDrop: async (e, toIdx) => {
      e.preventDefault();
      setDragOver(null);
      const fromIdx = dragRef.current;
      if (fromIdx === null || fromIdx === toIdx) return;
      const arr = [...lista];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      const updated = arr.map((item, i) => ({ ...item, orden: i + 1 }));
      setLista(updated);
      try {
        await axios.put(`${API_URL}/${endpoint}/reordenar`, {
          items: updated.map(x => ({ id: x.id, orden: x.orden })),
        });
      } catch { cargar(); }
    },
  });

  const crearSesionSpeaking = async () => {
    if (!spTitulo.trim()) return;
    setSpGuardando(true);
    try {
      await axios.post(`${API_URL}/speaking`, {
        tipo, nivel, titulo: spTitulo.trim(), descripcion: spDesc.trim() || null,
        fecha: spFecha || null, link_videollamada: spLink.trim() || null,
        max_alumnos: parseInt(spMaxAlumnos) || 1, tipo_sesion: spTipoSesion,
      });
      setModalCrear(false);
      setSpTitulo(''); setSpDesc(''); setSpFecha(''); setSpLink(''); setSpMaxAlumnos(1); setSpTipoSesion('individual');
      cargarSpeaking();
    } catch { /* silent */ }
    finally { setSpGuardando(false); }
  };

  const eliminarSesionSpeaking = async (id) => {
    if (!window.confirm('¿Eliminar sesión?')) return;
    await axios.delete(`${API_URL}/speaking/${id}`);
    cargarSpeaking();
  };

  const reservarSesion = async (sesionId) => {
    setReservandoId(sesionId);
    try {
      await axios.post(`${API_URL}/speaking/${sesionId}/reservar`, {});
      cargarSpeaking();
    } catch (e) { alert(e.response?.data?.error || 'Error al reservar'); }
    finally { setReservandoId(null); }
  };

  const cancelarReserva = async (sesionId) => {
    try {
      await axios.delete(`${API_URL}/speaking/${sesionId}/reservar`);
      cargarSpeaking();
    } catch { /* silent */ }
  };

  const confirmarReserva = async (reservaId, estado) => {
    try {
      await axios.put(`${API_URL}/speaking/reservas/${reservaId}/estado`, { estado });
      const updater = s => ({ ...s, reservas: (s.reservas || []).map(r => r.id === reservaId ? { ...r, estado } : r) });
      setSpeakingSesiones(prev => prev.map(s => s.id === modalReservas?.id ? updater(s) : s));
      setModalReservas(prev => prev ? updater(prev) : prev);
    } catch { /* silent */ }
  };

  const enviarEntrega = async () => {
    if (!spEntregaTitulo.trim() || !spEntregaLink.trim()) return;
    setEnviandoEntrega(true);
    try {
      await axios.post(`${API_URL}/speaking/entregas`, {
        tipo, nivel, titulo: spEntregaTitulo.trim(),
        descripcion: spEntregaDesc.trim() || null, link_media: spEntregaLink.trim(),
      });
      setSpEntregaTitulo(''); setSpEntregaDesc(''); setSpEntregaLink('');
      cargarEntregas();
    } catch (e) { alert(e.response?.data?.error || 'Error al enviar'); }
    finally { setEnviandoEntrega(false); }
  };

  const calificarEntrega = async (entregaId) => {
    setCalificandoEntrega(true);
    try {
      await axios.put(`${API_URL}/speaking/entregas/${entregaId}/calificar`, {
        nota: spEntregaNota || null, comentario_profesor: spEntregaComentario || null,
      });
      setModalEntrega(null);
      cargarEntregas();
    } catch { /* silent */ }
    finally { setCalificandoEntrega(false); }
  };

  const abrirNotas = async (sesion) => {
    setModalNotas(sesion);
    setSpGuardadoId(null);
    setSpErrorId(null);
    try {
      const res = await axios.get(`${API_URL}/speaking/${sesion.id}/alumnos`);
      setSpAlumnos(res.data);
      const init = {};
      res.data.forEach(a => { init[a.id] = { nota: a.nota ?? '', comentario: a.comentario ?? '' }; });
      setSpNotasEditar(init);
    } catch { setSpAlumnos([]); }
  };

  const guardarNota = async (alumnoId) => {
    const d = spNotasEditar[alumnoId];
    if (!d || d.nota === '' || d.nota === null || d.nota === undefined) return;
    const notaNum = parseFloat(d.nota);
    if (isNaN(notaNum)) return;
    setSpErrorId(null);
    try {
      await axios.put(`${API_URL}/speaking/${modalNotas.id}/nota/${alumnoId}`, {
        nota: notaNum, comentario: d.comentario || null,
      });
      setSpAlumnos(prev => prev.map(a => a.id === alumnoId ? { ...a, nota: notaNum, comentario: d.comentario || null } : a));
      setSpGuardadoId(alumnoId);
      setTimeout(() => setSpGuardadoId(null), 2000);
    } catch (e) {
      setSpErrorId(alumnoId);
      setTimeout(() => setSpErrorId(null), 3000);
    }
  };

  const examenesAll = evaluaciones.filter(ev => !ev.subtipo || ev.subtipo === 'examen').sort((a, b) => (a.orden || 0) - (b.orden || 0));
  const actividadesAll = evaluaciones.filter(ev => ev.subtipo === 'actividad').sort((a, b) => (a.orden || 0) - (b.orden || 0));
  const examenesFiltrados = categoriaActiva === 'Todas' ? examenesAll : examenesAll.filter(ev => ev.categoria === categoriaActiva);
  const actividadesFiltradas = categoriaActiva === 'Todas' ? actividadesAll : actividadesAll.filter(ev => ev.categoria === categoriaActiva);

  const dragAct  = makeDragHandlers(actividadesAll,  (arr) => setEvaluaciones(prev => [...prev.filter(e => e.subtipo !== 'actividad'), ...arr]), dragActIdx,  setDragOverAct,  'actividades');
  const dragExam = makeDragHandlers(examenesAll, (arr) => setEvaluaciones(prev => [...prev.filter(e => e.subtipo === 'actividad'), ...arr]),  dragExamIdx, setDragOverExam, 'evaluaciones');

  const iconoCat = { Writing: '✍️', Listening: '🎧', Reading: '📖', Speaking: '🎙️' };
  const subtipoLabel = (ev) => {
    if (ev.subtipo_eval === 'redaccion') return '✍️ Redacción';
    if (ev.categoria === 'Listening') return '🎧 Listening';
    if (ev.categoria === 'Reading') return '📖 Reading';
    return null;
  };

  const renderCard = (ev, etiqueta, index, dragHandlers, dragOverIdx) => (
    <div key={ev.id}
      draggable={esProfesor}
      onDragStart={esProfesor ? (e) => dragHandlers.onDragStart(e, index) : undefined}
      onDragOver={esProfesor ? (e) => dragHandlers.onDragOver(e, index) : undefined}
      onDrop={esProfesor ? (e) => dragHandlers.onDrop(e, index) : undefined}
      onDragEnd={esProfesor ? dragHandlers.onDragEnd : undefined}
      style={{ background: tema.bgCard, border: `2px solid ${dragOverIdx === index ? tema.accent : tema.border}`, borderRadius: '10px', padding: '16px 20px', cursor: esProfesor ? 'grab' : 'default', transform: dragOverIdx === index ? 'scale(1.01)' : 'scale(1)', transition: 'border-color 0.15s, transform 0.1s' }}>
      <div className="card-row">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
            {esProfesor && <span style={{ color: tema.muted, fontSize: '16px', cursor: 'grab', flexShrink: 0 }}>⠿</span>}
            <p style={{ color: !ev.activa ? tema.muted : tema.text, fontWeight: 600, fontFamily: 'Inter, sans-serif', margin: 0 }}>{ev.titulo}</p>
            {esProfesor && !ev.activa && (
              <span style={{ background: 'rgba(150,150,150,0.15)', color: tema.muted, fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>Oculta</span>
            )}
            {subtipoLabel(ev) && (
              <span style={{ background: `${tema.accent}15`, color: tema.accent, fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>{subtipoLabel(ev)}</span>
            )}
          </div>
          {ev.descripcion && <p style={{ color: tema.muted, fontSize: '13px' }}>{ev.descripcion}</p>}
          <div style={{ display: 'flex', gap: '12px', marginTop: '4px', flexWrap: 'wrap' }}>
            {ev.fecha && (
              <span style={{ color: tema.accent, fontSize: '12px', fontWeight: 600 }}>
                📅 {new Date(ev.fecha + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
            <span style={{ color: tema.muted, fontSize: '11px' }}>Creado: {new Date(ev.fecha_creacion).toLocaleDateString('es-ES')}</span>
          </div>
        </div>
        <div className="card-actions">
          {!esProfesor && (
            <button onClick={() => abrirDudas(ev)} title="Dudas"
              style={{ background: ev.dudas_sin_leer > 0 ? 'rgba(231,76,60,0.08)' : `${tema.accent}10`, border: `1px solid ${ev.dudas_sin_leer > 0 ? 'rgba(231,76,60,0.3)' : tema.accent + '22'}`, color: ev.dudas_sin_leer > 0 ? '#e74c3c' : tema.accent, padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: ev.dudas_sin_leer > 0 ? 700 : 400, position: 'relative' }}>
              <ChatBubbleLeftRightIcon style={{ width: 16, height: 16 }} />
              {ev.dudas_sin_leer > 0 ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ background: '#e74c3c', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '10px', lineHeight: '16px' }}>{ev.dudas_sin_leer}</span>
                  respuesta{ev.dudas_sin_leer > 1 ? 's' : ''} nueva{ev.dudas_sin_leer > 1 ? 's' : ''}
                </span>
              ) : 'Dudas'}
            </button>
          )}
          {!esProfesor && (
            ev.subtipo_eval === 'redaccion' ? (
              ev.entrega_redaccion?.estado === 'corregida' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Inter, sans-serif', color: ev.entrega_redaccion.nota >= 8.5 ? '#7C3AED' : ev.entrega_redaccion.nota >= 7 ? '#16a34a' : ev.entrega_redaccion.nota >= 5 ? '#d97706' : '#dc2626' }}>
                    {ev.entrega_redaccion.nota}/10
                  </span>
                  <button onClick={() => navigate(`/${tipo}/${nivel}/evaluacion/${ev.id}`)}
                    style={{ background: `${tema.accent}18`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '9px 20px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    Ver corrección
                  </button>
                </div>
              ) : ev.entrega_redaccion?.estado === 'pendiente' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.3)', color: '#d97706', padding: '8px 16px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    ⏳ Pendiente de evaluar
                  </span>
                  <button onClick={() => navigate(`/${tipo}/${nivel}/evaluacion/${ev.id}`)}
                    style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '8px 14px', borderRadius: '50px', fontSize: '12px', cursor: 'pointer' }}>
                    Ver entrega
                  </button>
                </div>
              ) : (
                <button onClick={() => navigate(`/${tipo}/${nivel}/evaluacion/${ev.id}`)}
                  style={{ background: 'linear-gradient(135deg, #4CAF84 0%, #2E8B57 100%)', border: 'none', color: '#fff', padding: '9px 20px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(76,175,132,0.4)' }}>
                  ✍️ Redacción
                </button>
              )
            ) : ev.intento ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Inter, sans-serif', color: ev.intento.puntuacion >= 85 ? '#7C3AED' : ev.intento.puntuacion >= 70 ? '#16a34a' : ev.intento.puntuacion >= 50 ? '#d97706' : '#dc2626' }}>
                  {ev.intento.puntuacion}%
                </span>
                <button onClick={() => navigate(`/${tipo}/${nivel}/evaluacion/${ev.id}`)}
                  style={{ background: `${tema.accent}18`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '9px 20px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                  Revisar
                </button>
              </div>
            ) : (
              <button onClick={() => navigate(`/${tipo}/${nivel}/evaluacion/${ev.id}`)}
                style={{ background: 'linear-gradient(135deg, #4CAF84 0%, #2E8B57 100%)', border: 'none', color: '#fff', padding: '9px 20px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(76,175,132,0.4)' }}>
                Hacer {etiqueta.toLowerCase()}
              </button>
            )
          )}
          {esProfesor && (
            <>
              <button onClick={() => abrirDudas(ev)} title="Ver dudas"
                style={{ background: `${tema.accent}10`, border: `1px solid ${tema.accent}22`, color: tema.accent, padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <ChatBubbleLeftRightIcon style={{ width: 17, height: 17 }} />
              </button>
              <button onClick={() => navigate(`/${tipo}/${nivel}/evaluacion/${ev.id}/resultados`)}
                style={{ background: `${tema.accent}22`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Ver notas
              </button>
              <button onClick={() => navigate(`/${tipo}/${nivel}/editar-evaluacion/${ev.id}`)} title={`Editar ${etiqueta.toLowerCase()}`}
                style={{ background: `${tema.accent}12`, border: `1px solid ${tema.accent}33`, color: tema.accent, padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <PencilSquareIcon style={{ width: 17, height: 17 }} />
              </button>
              <button onClick={() => guardarComoPlantilla(ev)} title="Guardar como plantilla"
                disabled={guardandoPlantillaId === ev.id}
                style={{ background: plantillaExitoId === ev.id ? 'rgba(39,174,96,0.12)' : 'rgba(100,100,200,0.08)', border: `1px solid ${plantillaExitoId === ev.id ? 'rgba(39,174,96,0.4)' : 'rgba(100,100,200,0.2)'}`, color: plantillaExitoId === ev.id ? '#27ae60' : tema.muted, padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <BookmarkIcon style={{ width: 17, height: 17 }} />
              </button>
              <button onClick={() => toggleOcultar(ev)} title={ev.activa ? 'Ocultar a alumnos' : 'Mostrar a alumnos'}
                style={{ background: ev.activa ? `${tema.accent}12` : 'rgba(150,150,150,0.1)', border: `1px solid ${ev.activa ? tema.accent + '33' : '#88888833'}`, color: ev.activa ? tema.accent : tema.muted, padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {ev.activa ? <EyeIcon style={{ width: 17, height: 17 }} /> : <EyeSlashIcon style={{ width: 17, height: 17 }} />}
              </button>
              <button onClick={() => eliminar(ev.id)} title={`Eliminar ${etiqueta.toLowerCase()}`}
                style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', color: '#e74c3c', padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <TrashIcon style={{ width: 17, height: 17 }} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const emptyState = (icono, texto) => (
    <div style={{ color: tema.muted, padding: '32px', textAlign: 'center', background: tema.bgCard, borderRadius: '12px', marginBottom: '28px' }}>
      <div style={{ fontSize: '36px', marginBottom: '8px' }}>{icono}</div>
      <p>{texto}</p>
    </div>
  );

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' };

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content">
          <div className="fade-in">
            <div className="section-header">
              <div>
                <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>✏️ Actividades / Exámenes</h2>
                <p style={{ color: tema.muted, fontSize: '14px' }}>Actividades y exámenes para {nivel}</p>
              </div>
              {esProfesor && categoriaActiva !== 'Speaking' && (
                <div className="section-header-actions">
                  <button onClick={() => navigate(`/${tipo}/${nivel}/plantillas`)}
                    style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '10px 18px', borderRadius: '50px', fontSize: '14px', cursor: 'pointer' }}>
                    📋 Plantillas
                  </button>
                  <button onClick={() => navigate(`/${tipo}/${nivel}/crear-actividad`)}
                    style={{ background: `${tema.accent}22`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '10px 18px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                    + Crear actividad
                  </button>
                  <button onClick={() => navigate(`/${tipo}/${nivel}/crear-evaluacion`)}
                    style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 18px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                    + Crear examen
                  </button>
                </div>
              )}
              {esProfesor && categoriaActiva === 'Speaking' && (
                <div className="section-header-actions">
                  <button onClick={() => { setSpTitulo(''); setSpDesc(''); setSpFecha(''); setModalCrear(true); }}
                    style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 18px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                    + Nueva sesión Speaking
                  </button>
                </div>
              )}
            </div>

            <div className="tabs-row" style={{ borderBottom: `1px solid ${tema.border}` }}>
              {CATEGORIAS.map(cat => (
                <button key={cat} onClick={() => setCategoriaActiva(cat)}
                  style={{
                    background: 'none', border: 'none',
                    borderBottom: categoriaActiva === cat ? `2px solid ${tema.accent}` : '2px solid transparent',
                    color: categoriaActiva === cat ? tema.accent : tema.muted,
                    padding: '10px 20px', fontSize: '14px', fontWeight: categoriaActiva === cat ? 700 : 400,
                    cursor: 'pointer', transition: 'all 0.15s', marginBottom: '-1px',
                  }}>
                  {iconoCat[cat] ? `${iconoCat[cat]} ` : ''}{cat}
                </button>
              ))}
            </div>

            {error && (
              <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '8px', padding: '12px', color: '#e74c3c', marginBottom: '20px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            {/* ── Speaking ───────────────────────────────────────── */}
            {categoriaActiva === 'Speaking' ? (
              <div style={{ marginTop: '24px' }}>
                {/* Sub-tabs */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: `1px solid ${tema.border}`, paddingBottom: '0' }}>
                  {[{ id: 'sesiones', label: '📅 Sesiones' }, { id: 'grabaciones', label: '🎙️ Grabaciones' }].map(t => (
                    <button key={t.id} onClick={() => setSpeakingTab(t.id)}
                      style={{ background: 'none', border: 'none', borderBottom: speakingTab === t.id ? `2px solid ${tema.accent}` : '2px solid transparent', color: speakingTab === t.id ? tema.accent : tema.muted, padding: '8px 18px', fontSize: '14px', fontWeight: speakingTab === t.id ? 700 : 400, cursor: 'pointer', marginBottom: '-1px' }}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ── Pestaña Sesiones (C + A) ── */}
                {speakingTab === 'sesiones' && (
                  <>
                    {speakingCargando ? (
                      <div style={{ color: tema.muted, padding: '24px' }}>Cargando...</div>
                    ) : speakingSesiones.length === 0 ? (
                      emptyState('📅', esProfesor ? 'Crea la primera sesión de Speaking' : 'No hay sesiones disponibles aún')
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {speakingSesiones.map(s => {
                          const miReserva = s.mi_reserva;
                          const plazasLibres = s.plazas_libres ?? (s.max_alumnos || 1);
                          const reservasActivas = (s.reservas || []).filter(r => r.estado !== 'cancelada');
                          return (
                            <div key={s.id} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '18px 20px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                    <p style={{ color: tema.text, fontWeight: 700, fontFamily: 'Inter, sans-serif', margin: 0 }}>{s.titulo}</p>
                                    <span style={{ background: s.tipo_sesion === 'grupal' ? `${tema.accent}18` : 'rgba(39,174,96,0.12)', color: s.tipo_sesion === 'grupal' ? tema.accent : '#27ae60', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                                      {s.tipo_sesion === 'grupal' ? '👥 Grupal' : '👤 Individual'}
                                    </span>
                                    {s.max_alumnos > 0 && <span style={{ color: tema.muted, fontSize: '11px' }}>· {esProfesor ? `${reservasActivas.length}/${s.max_alumnos} plazas` : `${plazasLibres} plaza${plazasLibres !== 1 ? 's' : ''} libre${plazasLibres !== 1 ? 's' : ''}`}</span>}
                                  </div>
                                  {s.descripcion && <p style={{ color: tema.muted, fontSize: '13px', margin: '0 0 6px' }}>{s.descripcion}</p>}
                                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {s.fecha && <span style={{ color: tema.accent, fontSize: '12px', fontWeight: 600 }}>📅 {new Date(s.fecha + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                    {s.link_videollamada && (
                                      <a href={s.link_videollamada} target="_blank" rel="noopener noreferrer"
                                        style={{ color: '#27ae60', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}>
                                        🎥 Unirse a la videollamada
                                      </a>
                                    )}
                                  </div>
                                  {/* Nota del alumno */}
                                  {!esProfesor && s.mi_nota && (
                                    <p style={{ color: tema.accent, fontSize: '13px', fontWeight: 600, marginTop: '8px' }}>
                                      Nota: <span style={{ color: s.mi_nota.nota >= 5 ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>{s.mi_nota.nota}/10</span>
                                      {s.mi_nota.comentario && <span style={{ color: tema.muted, fontWeight: 400 }}> — {s.mi_nota.comentario}</span>}
                                    </p>
                                  )}
                                </div>

                                {/* Acciones profesor */}
                                {esProfesor && (
                                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                    <button onClick={() => { setModalReservas(s); }}
                                      style={{ background: `${tema.accent}15`, border: `1px solid ${tema.accent}33`, color: tema.accent, padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                                      Reservas ({reservasActivas.length})
                                    </button>
                                    <button onClick={() => abrirNotas(s)}
                                      style={{ background: `${tema.accent}22`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                                      Notas
                                    </button>
                                    <button onClick={() => eliminarSesionSpeaking(s.id)}
                                      style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', color: '#e74c3c', padding: '7px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                      <TrashIcon style={{ width: 15, height: 15 }} />
                                    </button>
                                  </div>
                                )}

                                {/* Acciones alumno */}
                                {!esProfesor && (
                                  <div style={{ flexShrink: 0 }}>
                                    {miReserva ? (
                                      <div style={{ textAlign: 'right' }}>
                                        <span style={{
                                          display: 'inline-block', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                                          background: miReserva.estado === 'confirmada' ? 'rgba(39,174,96,0.12)' : miReserva.estado === 'cancelada' ? 'rgba(231,76,60,0.08)' : 'rgba(234,179,8,0.12)',
                                          color: miReserva.estado === 'confirmada' ? '#27ae60' : miReserva.estado === 'cancelada' ? '#e74c3c' : '#d97706',
                                        }}>
                                          {miReserva.estado === 'confirmada' ? '✓ Confirmada' : miReserva.estado === 'cancelada' ? 'Cancelada' : '⏳ Pendiente'}
                                        </span>
                                        {miReserva.estado !== 'cancelada' && (
                                          <button onClick={() => cancelarReserva(s.id)}
                                            style={{ display: 'block', marginTop: '6px', background: 'none', border: 'none', color: tema.muted, fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>
                                            Cancelar reserva
                                          </button>
                                        )}
                                      </div>
                                    ) : s.tipo_sesion === 'individual' ? (
                                      <button onClick={() => reservarSesion(s.id)} disabled={reservandoId === s.id || plazasLibres === 0}
                                        style={{ background: plazasLibres > 0 ? tema.primary : tema.border, border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: plazasLibres > 0 ? 'pointer' : 'default', opacity: reservandoId === s.id ? 0.7 : 1 }}>
                                        {reservandoId === s.id ? '...' : plazasLibres > 0 ? '📅 Reservar' : 'Sin plazas'}
                                      </button>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* ── Pestaña Grabaciones (B) ── */}
                {speakingTab === 'grabaciones' && (
                  <>
                    {/* Formulario alumno */}
                    {!esProfesor && (
                      <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                        <h4 style={{ color: tema.text, margin: '0 0 14px', fontSize: '15px' }}>🎙️ Enviar una grabación</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div>
                            <label style={labelStyle}>Título *</label>
                            <input style={inputStyle} value={spEntregaTitulo} onChange={e => setSpEntregaTitulo(e.target.value)} placeholder="Ej: Presentación personal" />
                          </div>
                          <div>
                            <label style={labelStyle}>Enlace a la grabación * (Drive, YouTube, etc.)</label>
                            <input style={inputStyle} value={spEntregaLink} onChange={e => setSpEntregaLink(e.target.value)} placeholder="https://..." />
                          </div>
                          <div>
                            <label style={labelStyle}>Descripción (opcional)</label>
                            <textarea style={{ ...inputStyle, height: '60px', resize: 'vertical' }} value={spEntregaDesc} onChange={e => setSpEntregaDesc(e.target.value)} placeholder="Contexto o notas para el profesor..." />
                          </div>
                          <button onClick={enviarEntrega} disabled={enviandoEntrega || !spEntregaTitulo.trim() || !spEntregaLink.trim()}
                            style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', opacity: enviandoEntrega ? 0.7 : 1 }}>
                            {enviandoEntrega ? 'Enviando...' : '📤 Enviar grabación'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Lista de entregas */}
                    {entregasCargando ? (
                      <div style={{ color: tema.muted, padding: '24px' }}>Cargando...</div>
                    ) : speakingEntregas.length === 0 ? (
                      emptyState('🎙️', esProfesor ? 'Los alumnos aún no han enviado grabaciones' : 'Aún no has enviado ninguna grabación')
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {esProfesor && <p style={{ color: tema.muted, fontSize: '13px', margin: '0 0 4px' }}>{speakingEntregas.length} grabación{speakingEntregas.length !== 1 ? 'es' : ''} enviada{speakingEntregas.length !== 1 ? 's' : ''}</p>}
                        {speakingEntregas.map(e => (
                          <div key={e.id} style={{ background: tema.bgCard, border: `1px solid ${e.estado === 'revisada' ? tema.accent + '44' : tema.border}`, borderRadius: '10px', padding: '16px 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {esProfesor && <p style={{ color: tema.muted, fontSize: '12px', margin: '0 0 2px', fontWeight: 600 }}>{e.nombre} · {e.email}</p>}
                                <p style={{ color: tema.text, fontWeight: 600, margin: '0 0 4px' }}>{e.titulo}</p>
                                {e.descripcion && <p style={{ color: tema.muted, fontSize: '12px', margin: '0 0 6px' }}>{e.descripcion}</p>}
                                <a href={e.link_media} target="_blank" rel="noopener noreferrer"
                                  style={{ color: tema.accent, fontSize: '13px', fontWeight: 600 }}>🔗 Ver grabación</a>
                                <p style={{ color: tema.muted, fontSize: '11px', margin: '6px 0 0' }}>
                                  {new Date(e.fecha_entrega).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                                {e.estado === 'revisada' && (
                                  <div style={{ marginTop: '8px', padding: '8px 12px', background: `${tema.accent}10`, borderRadius: '8px', border: `1px solid ${tema.accent}30` }}>
                                    {e.nota && <p style={{ color: '#27ae60', fontWeight: 700, fontSize: '13px', margin: '0 0 2px' }}>Nota: {e.nota}</p>}
                                    {e.comentario_profesor && <p style={{ color: tema.text, fontSize: '13px', margin: 0 }}>{e.comentario_profesor}</p>}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'flex-start' }}>
                                <span style={{ background: e.estado === 'revisada' ? 'rgba(39,174,96,0.12)' : 'rgba(234,179,8,0.12)', color: e.estado === 'revisada' ? '#27ae60' : '#d97706', fontSize: '11px', padding: '3px 10px', borderRadius: '10px', fontWeight: 600 }}>
                                  {e.estado === 'revisada' ? '✓ Revisada' : '⏳ Pendiente'}
                                </span>
                                {esProfesor && (
                                  <button onClick={() => { setModalEntrega(e); setSpEntregaNota(e.nota || ''); setSpEntregaComentario(e.comentario_profesor || ''); }}
                                    style={{ background: `${tema.accent}22`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                                    {e.estado === 'revisada' ? 'Editar nota' : 'Calificar'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Sección Actividades */}
                <h3 style={{ color: tema.accent, fontSize: '16px', marginBottom: '14px', marginTop: '24px', fontFamily: 'Inter, sans-serif' }}>
                  Actividades{categoriaActiva !== 'Todas' ? ` — ${categoriaActiva}` : ''}
                </h3>
                {cargando ? (
                  <div style={{ color: tema.muted, padding: '24px' }}>Cargando...</div>
                ) : actividadesFiltradas.length === 0 ? (
                  emptyState('📋', `No hay actividades${categoriaActiva !== 'Todas' ? ` de ${categoriaActiva}` : ''} aún`)
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                    {esProfesor && actividadesFiltradas.length > 1 && <p style={{ color: tema.muted, fontSize: '12px', textAlign: 'center', marginBottom: '2px' }}>⠿ Arrastra para reordenar</p>}
                    {actividadesFiltradas.map((ev, i) => renderCard(ev, 'Actividad', i, dragAct, dragOverAct))}
                  </div>
                )}

                {/* Sección Examenes */}
                <h3 style={{ color: tema.accent, fontSize: '16px', marginBottom: '14px', fontFamily: 'Inter, sans-serif' }}>
                  Exámenes{categoriaActiva !== 'Todas' ? ` — ${categoriaActiva}` : ''}
                </h3>
                {!cargando && (
                  examenesFiltrados.length === 0 ? (
                    emptyState('🧪', `No hay exámenes${categoriaActiva !== 'Todas' ? ` de ${categoriaActiva}` : ''} aún`)
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {esProfesor && examenesFiltrados.length > 1 && <p style={{ color: tema.muted, fontSize: '12px', textAlign: 'center', marginBottom: '2px' }}>⠿ Arrastra para reordenar</p>}
                      {examenesFiltrados.map((ev, i) => renderCard(ev, 'Examen', i, dragExam, dragOverExam))}
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Modal: Crear sesión Speaking */}
      {modalCrear && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '460px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: tema.text, fontSize: '18px', margin: 0 }}>🎙️ Nueva sesión de Speaking</h3>
              <button onClick={() => setModalCrear(false)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Título *</label>
              <input style={inputStyle} value={spTitulo} onChange={e => setSpTitulo(e.target.value)} placeholder="Ej: Speaking 1 — Presentaciones" autoFocus />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Descripción (opcional)</label>
              <input style={inputStyle} value={spDesc} onChange={e => setSpDesc(e.target.value)} placeholder="Ej: Videollamada del 10 de mayo" />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>📅 Fecha de la sesión (opcional)</label>
              <input type="date" style={inputStyle} value={spFecha} onChange={e => setSpFecha(e.target.value)} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>🎥 Enlace videollamada (opcional)</label>
              <input style={inputStyle} value={spLink} onChange={e => setSpLink(e.target.value)} placeholder="https://meet.google.com/..." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
              <div>
                <label style={labelStyle}>👥 Tipo de sesión</label>
                <select style={inputStyle} value={spTipoSesion} onChange={e => setSpTipoSesion(e.target.value)}>
                  <option value="individual">👤 Individual</option>
                  <option value="grupal">👥 Grupal</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Plazas máximas</label>
                <input type="number" min="1" max="50" style={inputStyle} value={spMaxAlumnos} onChange={e => setSpMaxAlumnos(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={crearSesionSpeaking} disabled={!spTitulo.trim() || spGuardando}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1 }}>
                {spGuardando ? 'Guardando...' : 'Crear sesión'}
              </button>
              <button onClick={() => setModalCrear(false)}
                style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '10px 18px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Dudas — diseño chat */}
      {modalDudas && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '20px', width: '100%', maxWidth: '600px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Cabecera */}
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${tema.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: `${tema.accent}08` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '10px', background: `${tema.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChatBubbleLeftRightIcon style={{ width: 18, height: 18, color: tema.accent }} />
                </div>
                <div>
                  <p style={{ color: tema.text, fontWeight: 700, fontSize: '15px', margin: 0 }}>Dudas</p>
                  <p style={{ color: tema.muted, fontSize: '11px', margin: 0 }}>{modalDudas.titulo}</p>
                </div>
              </div>
              <button onClick={() => setModalDudas(null)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer', padding: '4px' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>

            {/* Hilo de dudas */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {dudasCargando ? (
                <div style={{ textAlign: 'center', color: tema.muted, padding: '32px' }}>Cargando...</div>
              ) : dudas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: tema.muted }}>
                  <div style={{ fontSize: '40px', marginBottom: '10px' }}>💬</div>
                  <p style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 4px', color: tema.text }}>Sin dudas todavía</p>
                  <p style={{ fontSize: '13px', margin: 0 }}>{esProfesor ? 'Los alumnos aún no han enviado dudas' : 'Escribe tu duda abajo y el profesor te responderá'}</p>
                </div>
              ) : dudas.map(d => (
                <div key={d.id}>
                  {/* Pregunta del alumno */}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: `${tema.accent}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tema.accent, fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
                      {d.alumno_nombre?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                        <span style={{ color: tema.text, fontWeight: 600, fontSize: '13px' }}>{d.alumno_nombre}</span>
                        <span style={{ color: tema.muted, fontSize: '11px' }}>{new Date(d.fecha_pregunta).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                        {!d.respondida && <span style={{ background: 'rgba(234,179,8,0.15)', color: '#d97706', fontSize: '10px', padding: '2px 8px', borderRadius: '8px', fontWeight: 600 }}>⏳ Sin responder</span>}
                      </div>
                      <div style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '4px 14px 14px 14px', padding: '10px 14px' }}>
                        <p style={{ color: tema.text, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>{d.pregunta}</p>
                      </div>
                    </div>
                    {esProfesor && (
                      <button onClick={() => eliminarDuda(d.id)} title="Eliminar"
                        style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer', padding: '6px', opacity: 0.5, flexShrink: 0 }}>
                        <TrashIcon style={{ width: 14, height: 14 }} />
                      </button>
                    )}
                  </div>

                  {/* Respuesta del profesor */}
                  {d.respondida && d.respuesta && respondiendoId !== d.id && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', paddingLeft: '44px', marginTop: '10px' }}>
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${tema.primary}, ${tema.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
                        P
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                          <span style={{ color: tema.text, fontWeight: 600, fontSize: '13px' }}>Profesor</span>
                          {d.fecha_respuesta && <span style={{ color: tema.muted, fontSize: '11px' }}>{new Date(d.fecha_respuesta).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>}
                          {esProfesor && (
                            <button onClick={() => { setRespondiendoId(d.id); setTextoRespuesta(d.respuesta || ''); }}
                              style={{ background: 'none', border: 'none', color: tema.muted, fontSize: '11px', cursor: 'pointer', padding: '0', textDecoration: 'underline' }}>
                              Editar
                            </button>
                          )}
                        </div>
                        <div style={{ background: `${tema.accent}14`, border: `1px solid ${tema.accent}30`, borderRadius: '14px 4px 14px 14px', padding: '10px 14px' }}>
                          <p style={{ color: tema.text, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>{d.respuesta}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Formulario de respuesta (profesor) */}
                  {esProfesor && (!d.respondida || respondiendoId === d.id) && (
                    <div style={{ paddingLeft: '44px', marginTop: '10px' }}>
                      {respondiendoId === d.id ? (
                        <div>
                          <textarea value={textoRespuesta} onChange={e => setTextoRespuesta(e.target.value)}
                            placeholder="Escribe tu respuesta..."
                            style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', background: tema.bg, border: `1px solid ${tema.accent}`, color: tema.text, fontSize: '13px', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box', lineHeight: '1.5' }}
                            autoFocus />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button onClick={() => responderDuda(d.id)} disabled={guardandoRespuesta || !textoRespuesta.trim()}
                              style={{ background: tema.primary, border: 'none', color: '#fff', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                              {guardandoRespuesta ? 'Enviando...' : '↩ Enviar respuesta'}
                            </button>
                            <button onClick={() => { setRespondiendoId(null); setTextoRespuesta(''); }}
                              style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '8px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setRespondiendoId(d.id); setTextoRespuesta(''); }}
                          style={{ background: `${tema.accent}15`, border: `1px solid ${tema.accent}33`, color: tema.accent, padding: '7px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                          ↩ Responder
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Input nueva duda (solo alumnos) */}
            {!esProfesor && (
              <div style={{ borderTop: `1px solid ${tema.border}`, padding: '14px 22px', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <textarea value={nuevaDuda} onChange={e => setNuevaDuda(e.target.value)}
                    placeholder="Escribe tu duda aquí..."
                    rows={2}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '12px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '13px', resize: 'none', boxSizing: 'border-box', lineHeight: '1.5', outline: 'none' }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarDuda(); } }}
                  />
                  <button onClick={enviarDuda} disabled={enviandoDuda || !nuevaDuda.trim()}
                    style={{ background: nuevaDuda.trim() ? tema.primary : tema.border, border: 'none', color: '#fff', padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: nuevaDuda.trim() ? 'pointer' : 'default', transition: 'background 0.2s', whiteSpace: 'nowrap' }}>
                    {enviandoDuda ? '...' : 'Enviar'}
                  </button>
                </div>
                <p style={{ color: tema.muted, fontSize: '11px', margin: '6px 0 0' }}>Enter para enviar · Shift+Enter para nueva línea</p>
              </div>
            )}

            {esProfesor && (
              <div style={{ borderTop: `1px solid ${tema.border}`, padding: '12px 22px', flexShrink: 0, textAlign: 'right' }}>
                <button onClick={() => setModalDudas(null)}
                  style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '8px 18px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Reservas de una sesión (profesor) */}
      {modalReservas && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexShrink: 0 }}>
              <h3 style={{ color: tema.text, fontSize: '18px', margin: 0 }}>📅 Reservas</h3>
              <button onClick={() => setModalReservas(null)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>
            <p style={{ color: tema.muted, fontSize: '13px', marginBottom: '18px', flexShrink: 0 }}>{modalReservas.titulo}</p>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {(modalReservas.reservas || []).length === 0 ? (
                <p style={{ color: tema.muted, textAlign: 'center', padding: '24px' }}>No hay reservas aún</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(modalReservas.reservas || []).map(r => (
                    <div key={r.id} style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <p style={{ color: tema.text, fontWeight: 600, fontSize: '14px', margin: 0 }}>{r.nombre}</p>
                        <p style={{ color: tema.muted, fontSize: '12px', margin: '2px 0 0' }}>{r.email}</p>
                        {r.notas && <p style={{ color: tema.muted, fontSize: '12px', fontStyle: 'italic', margin: '4px 0 0' }}>"{r.notas}"</p>}
                      </div>
                      <span style={{ background: r.estado === 'confirmada' ? 'rgba(39,174,96,0.12)' : r.estado === 'cancelada' ? 'rgba(231,76,60,0.08)' : 'rgba(234,179,8,0.12)', color: r.estado === 'confirmada' ? '#27ae60' : r.estado === 'cancelada' ? '#e74c3c' : '#d97706', fontSize: '11px', padding: '3px 10px', borderRadius: '10px', fontWeight: 600, flexShrink: 0 }}>
                        {r.estado === 'confirmada' ? '✓ Confirmada' : r.estado === 'cancelada' ? 'Cancelada' : '⏳ Pendiente'}
                      </span>
                      {r.estado !== 'cancelada' && (
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          {r.estado !== 'confirmada' && (
                            <button onClick={() => confirmarReserva(r.id, 'confirmada')}
                              style={{ background: 'rgba(39,174,96,0.12)', border: '1px solid rgba(39,174,96,0.3)', color: '#27ae60', padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                              ✓ Confirmar
                            </button>
                          )}
                          <button onClick={() => confirmarReserva(r.id, 'cancelada')}
                            style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', color: '#e74c3c', padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginTop: '16px', flexShrink: 0 }}>
              <button onClick={() => setModalReservas(null)}
                style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '9px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Calificar entrega de grabación (profesor) */}
      {modalEntrega && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '460px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h3 style={{ color: tema.text, fontSize: '18px', margin: 0 }}>🎙️ Calificar grabación</h3>
              <button onClick={() => setModalEntrega(null)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>
            <p style={{ color: tema.muted, fontSize: '13px', marginBottom: '4px' }}>{modalEntrega.nombre}</p>
            <p style={{ color: tema.text, fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>{modalEntrega.titulo}</p>
            <a href={modalEntrega.link_media} target="_blank" rel="noopener noreferrer"
              style={{ color: tema.accent, fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '20px' }}>
              🔗 Ver grabación
            </a>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Nota (opcional)</label>
              <input type="text" style={inputStyle} value={spEntregaNota} onChange={e => setSpEntregaNota(e.target.value)} placeholder="Ej: 8, Apto, Muy bien..." />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Comentario para el alumno (opcional)</label>
              <textarea style={{ ...inputStyle, height: '90px', resize: 'vertical' }} value={spEntregaComentario} onChange={e => setSpEntregaComentario(e.target.value)} placeholder="Observaciones, puntos de mejora..." />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => calificarEntrega(modalEntrega.id)} disabled={calificandoEntrega}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1, opacity: calificandoEntrega ? 0.7 : 1 }}>
                {calificandoEntrega ? 'Guardando...' : '✓ Guardar calificación'}
              </button>
              <button onClick={() => setModalEntrega(null)}
                style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '10px 18px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Asignar notas Speaking */}
      {modalNotas && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
              <h3 style={{ color: tema.text, fontSize: '18px', margin: 0 }}>🎙️ {modalNotas.titulo}</h3>
              <button onClick={() => setModalNotas(null)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>
            <p style={{ color: tema.muted, fontSize: '13px', marginBottom: '16px', flexShrink: 0 }}>Asigna una nota (0-10) a cada alumno evaluado por videollamada</p>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {spAlumnos.length === 0 ? (
                <p style={{ color: tema.muted, textAlign: 'center', padding: '24px' }}>No hay alumnos en este nivel</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {spAlumnos.map(a => (
                    <div key={a.id} style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '10px', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <p style={{ color: tema.text, fontWeight: 600, fontSize: '14px', margin: 0 }}>{a.nombre}</p>
                          <p style={{ color: tema.muted, fontSize: '12px' }}>{a.email}</p>
                        </div>
                        <input
                          type="number" min="0" max="10" step="0.5"
                          placeholder="Nota"
                          value={spNotasEditar[a.id]?.nota ?? ''}
                          onChange={e => setSpNotasEditar(prev => ({ ...prev, [a.id]: { ...prev[a.id], nota: e.target.value } }))}
                          style={{ ...inputStyle, width: '72px', padding: '6px 10px', textAlign: 'center' }}
                        />
                        <input
                          type="text"
                          placeholder="Comentario (opcional)"
                          value={spNotasEditar[a.id]?.comentario ?? ''}
                          onChange={e => setSpNotasEditar(prev => ({ ...prev, [a.id]: { ...prev[a.id], comentario: e.target.value } }))}
                          style={{ ...inputStyle, flex: 2, minWidth: '120px', padding: '6px 10px' }}
                        />
                        <button onClick={() => guardarNota(a.id)}
                          style={{
                            background: spGuardadoId === a.id ? '#27ae60' : spErrorId === a.id ? '#e74c3c' : tema.primary,
                            border: 'none', color: '#fff', padding: '7px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', minWidth: '72px', transition: 'background 0.2s'
                          }}>
                          {spGuardadoId === a.id ? '✓ Guardado' : spErrorId === a.id ? '✗ Error' : 'Guardar'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginTop: '16px', flexShrink: 0 }}>
              <button onClick={() => setModalNotas(null)}
                style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '9px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
